import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Icon } from '@/shared/icons';
import { AI_MODELS } from '@troia-v3/types';

/**
 * Painel "Testar agente" fixo à direita do formulário (mockup agent-form-03):
 * chat de pré-visualização que executa o agente REAL (tools/RAG) numa conversa
 * sandbox via `POST /agent-training/:agentId/simulate`. O `sessionId` mantém o
 * histórico entre turnos; ↻ zera a sessão.
 */

interface SimMessage {
  role: 'user' | 'agent';
  text: string;
  toolCalls?: string[];
}

interface SimulateResponse {
  sessionId: string;
  reply: string;
  toolCalls: string[];
  latencyMs: number;
}

export const AgentTestPanel: React.FC<{
  agentId: string;
  model: string;
  /** 'sidebar' = aside fixo à direita (desktop); 'sheet' = bottom-sheet mobile */
  variant?: 'sidebar' | 'sheet';
  onClose?: () => void;
}> = ({ agentId, model, variant = 'sidebar', onClose }) => {
  const [messages, setMessages] = useState<SimMessage[]>([]);
  const [sessionId, setSessionId] = useState<string | undefined>(undefined);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, loading]);

  const reset = () => {
    setMessages([]);
    setSessionId(undefined);
  };

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', text }]);
    setLoading(true);
    try {
      const response = await api.post(`agent-training/${agentId}/simulate`, {
        message: text,
        ...(sessionId ? { sessionId } : {}),
      });
      const data = response.data.data as SimulateResponse;
      setSessionId(data.sessionId);
      setMessages((prev) => [
        ...prev,
        {
          role: 'agent',
          text: data.reply || '(sem resposta — verifique gatilhos/modelo do agente)',
          ...(data.toolCalls.length > 0 ? { toolCalls: data.toolCalls } : {}),
        },
      ]);
    } catch (error) {
      toast.error('Falha ao simular — verifique a integração de IA do modelo');
      console.error('[AgentTestPanel] simulate failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const content = (
    <>
      {/* Header (mockup: tile roxo + "Testar agente" + modelo · pré-visualização + ↻) */}
      <div className="flex items-center gap-3 p-4 flex-none" style={{ borderBottom: '1px solid var(--theme-card-border)' }}>
        <span
          className="flex items-center justify-center flex-none rounded-[10px]"
          style={{ width: 34, height: 34, backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}
        >
          <Icon name="cpu" size={16} />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="text-[15px] font-semibold" style={{ color: 'var(--theme-card-text)' }}>Testar agente</h3>
          <p className="text-xs truncate" style={{ color: 'var(--theme-muted-text)' }}>
            {(AI_MODELS.find((m) => m.id === model)?.name ?? model)} · pré-visualização
          </p>
        </div>
        <button
          type="button"
          onClick={reset}
          className="w-8 h-8 rounded-lg flex items-center justify-center flex-none transition-colors"
          style={{ color: 'var(--theme-muted-text)' }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
          title="Reiniciar conversa"
          data-testid="agent-test-reset"
        >
          <Icon name="refresh" className="w-4 h-4" />
        </button>
        {variant === 'sheet' && (
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center flex-none transition-colors"
            style={{ color: 'var(--theme-muted-text)' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            title="Fechar"
            data-testid="agent-test-sheet-close"
          >
            <Icon name="x" className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Corpo do chat */}
      <div
        ref={bodyRef}
        className="flex-1 min-h-0 overflow-y-auto p-4 flex flex-col gap-2.5"
        style={{ backgroundColor: 'var(--theme-page-background)' }}
      >
        {messages.length === 0 && !loading && (
          <p className="text-xs text-center mt-8" style={{ color: 'var(--theme-muted-text)' }}>
            Simule uma conversa de cliente pra ver como o agente responde — com as
            ferramentas e bases reais configuradas nas abas.
          </p>
        )}
        {messages.map((message, index) => (
          <React.Fragment key={index}>
            {message.role === 'agent' && message.toolCalls && (
              <div className="flex flex-wrap gap-1.5">
                {message.toolCalls.map((tc, ti) => (
                  <span
                    key={ti}
                    className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold"
                    style={{ backgroundColor: 'var(--theme-surface-sunken, rgba(148,163,184,.16))', color: 'var(--theme-card-text)' }}
                  >
                    <Icon name="tool" className="w-3 h-3 flex-none" style={{ color: 'var(--theme-primary)' }} />
                    {tc}
                  </span>
                ))}
              </div>
            )}
            <div
              className="max-w-[85%] rounded-xl px-3 py-2 text-[13px] leading-snug whitespace-pre-wrap"
              style={
                message.role === 'user'
                  ? { alignSelf: 'flex-start', backgroundColor: 'var(--theme-card-background)', color: 'var(--theme-card-text)', border: '1px solid var(--theme-card-border)' }
                  : { alignSelf: 'flex-end', backgroundColor: 'var(--theme-primary)', color: '#ffffff' }
              }
            >
              {message.text}
            </div>
          </React.Fragment>
        ))}
        {loading && (
          <div
            className="self-end rounded-xl px-3 py-2 text-[13px]"
            style={{ backgroundColor: 'var(--theme-primary)', color: '#ffffff', opacity: 0.7 }}
          >
            <span className="inline-flex gap-1 items-center">
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-bounce" style={{ animationDelay: '0ms' }} />
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-bounce" style={{ animationDelay: '150ms' }} />
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-bounce" style={{ animationDelay: '300ms' }} />
            </span>
          </div>
        )}
      </div>

      {/* Composer (mockup: campo redondo + send circular roxo) */}
      <div className="flex items-center gap-2 p-3 flex-none" style={{ borderTop: '1px solid var(--theme-card-border)' }}>
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') void send(); }}
          placeholder="Simular mensagem do cliente..."
          disabled={loading}
          className="flex-1 min-w-0 h-[38px] rounded-full border px-4 text-sm outline-none disabled:opacity-60"
          style={{
            backgroundColor: 'var(--theme-input-background)',
            borderColor: 'var(--theme-input-border)',
            color: 'var(--theme-input-text)',
          }}
          onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--theme-input-focus)'; }}
          onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--theme-input-border)'; }}
          data-testid="agent-test-input"
        />
        <button
          type="button"
          onClick={() => void send()}
          disabled={loading || input.trim().length === 0}
          className="w-[38px] h-[38px] rounded-full flex items-center justify-center flex-none text-white disabled:opacity-50 transition-colors"
          style={{ backgroundColor: 'var(--theme-button-primary)' }}
          aria-label="Enviar"
          data-testid="agent-test-send"
        >
          <Icon name="send" className="w-4 h-4" />
        </button>
      </div>
    </>
  );

  // Mobile: bottom-sheet (#210 — aberto pelo FAB "Testar agente")
  if (variant === 'sheet') {
    return createPortal(
      <>
        {/* chat de teste tem input: NÃO fecha ao tocar fora (DS §Overlays dismissal) */}
        <div className="lg:hidden fixed inset-0 z-[90] bg-black/40" />
        <div
          className="lg:hidden fixed inset-x-0 bottom-0 z-[95] flex flex-col rounded-t-2xl overflow-hidden shadow-xl"
          style={{ maxHeight: '88vh', background: 'var(--theme-overlay-card-background)' }}
          data-testid="agent-test-sheet"
        >
          <div className="flex justify-center pt-2 pb-1 flex-none">
            <span className="w-9 h-1 rounded-full" style={{ backgroundColor: 'var(--theme-card-border)' }} />
          </div>
          {content}
        </div>
      </>,
      document.body
    );
  }

  return (
    <aside
      className="hidden lg:flex flex-col w-[320px] xl:w-[380px] flex-none border-l min-h-0"
      style={{ backgroundColor: 'var(--theme-card-background)', borderColor: 'var(--theme-card-border)' }}
      data-testid="agent-test-panel"
    >
      {content}
    </aside>
  );
};

AgentTestPanel.displayName = 'AgentTestPanel';
